import { FunctionComponent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import { Absence } from '@/types/Slots'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { useTranslation } from 'react-i18next'
import styles from './showAbsenceDialog.module.scss'
import { Daycare, DaycareType } from '@/types/Daycare'
import CancelAbsenceRegularSlot from '../../regularSlot/cancelAbsenceRegularSlot/cancelAbsenceRegularSlot'
import moment from 'moment-timezone'
import { CalendarMonth, ChildCareOutlined, DoDisturbOff, Forum, Message, Person } from '@mui/icons-material'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { isPastDate, isWithinEditionLimit, titleWithPastLabel } from '@/modules/helper/editionLimitHelper'

interface ShowAbsenceDialogProps {
    open: boolean
    handleClose: () => void
    absences: Absence[]
    daycare: Daycare
    refetchData: () => void
    canEditRegular: boolean
}

const ShowAbsenceDialog: FunctionComponent<ShowAbsenceDialogProps> = ({
    open,
    handleClose,
    absences,
    daycare,
    refetchData,
    canEditRegular
}: ShowAbsenceDialogProps) => {
    const { t } = useTranslation()

    const isRpeOrLaep: boolean = daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep

    const navigate: NavigateFunction = useNavigate()

    const absence: Absence = absences[0]

    const [isCancelAbsenceDialogOpen, setCancelAbsenceDialogOpen] = useState<boolean>(false)

    return (
        (absences.length > 0 && (
            <>
                <Dialog open={open} handleClose={handleClose}>
                    <DialogHeader title={titleWithPastLabel(t('absence'), isPastDate(absence.date), t)} />
                    <DialogBody className={styles.DialogBody}>
                        <div className={styles.Container}>
                            <CalendarMonth className={styles.Icon} />
                            <div className={styles.TextContainer}>
                                <p className={styles.FirstText}>
                                    {format(parse(absence.date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                                        locale: getLocalFromLanguage()
                                    })}
                                </p>
                                <p className={styles.SecondText}>
                                    {t('from') +
                                        ' ' +
                                        moment(absence.start, 'HH:mm:ss').format('HH:mm') +
                                        ' ' +
                                        t('to').toLowerCase() +
                                        ' ' +
                                        moment(absence.end, 'HH:mm:ss').format('HH:mm')}
                                </p>
                            </div>
                        </div>
                        <div className={styles.BreakLine}></div>
                        {isRpeOrLaep && absence.parentName && (
                            <div className={styles.SecondContainer}>
                                <Person
                                    className={styles.ClickableIcon}
                                    onClick={() => navigate(`/adults?parent=${absence.parentId}`)}
                                />
                                <p className={styles.Text}>{absence.parentName}</p>
                            </div>
                        )}
                        {absences.length > 0 &&
                            absences.map((abs: Absence, index: number) => (
                                <div className={styles.SecondContainer} key={index}>
                                    <ChildCareOutlined
                                        className={`${styles.Icon} ${styles.ClickableIcon}`}
                                        onClick={() => navigate(`/childs?child=${abs.childId}`)}
                                    />
                                    <p className={styles.Text}>
                                        {abs.childName + (abs.childComplement ? ' - ' + abs.childComplement : '')}
                                    </p>
                                </div>
                            ))}
                        {absences.map(
                            (abs: Absence) =>
                                (abs.parentComment || abs.daycareComment) && (
                                    <div key={abs.regularSlotId}>
                                        {absences.length > 1 && <p className={styles.Text}>{abs.childName}</p>}
                                        {abs.parentComment && (
                                            <div className={styles.SecondContainer}>
                                                <Message className={styles.Icon} />
                                                <p className={styles.Text} aria-label={t('absenceParentComment')}>
                                                    {abs.parentComment}
                                                </p>
                                            </div>
                                        )}
                                        {abs.daycareComment && (
                                            <div className={styles.SecondContainer}>
                                                <Forum className={styles.Icon} />
                                                <p className={styles.Text} aria-label={t('absenceDaycareComment')}>
                                                    {abs.daycareComment}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )
                        )}
                        <div className={styles.ButtonContainer}>
                            {canEditRegular && isWithinEditionLimit(absence.date) ? (
                                <>
                                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                                    <ButtonTextIcon
                                        type={'custom'}
                                        onClick={() => setCancelAbsenceDialogOpen(true)}
                                        customText={t('cancel')}
                                        customIcon={<DoDisturbOff />}
                                        className={styles.Button}
                                    />
                                </>
                            ) : (
                                <ButtonTextIcon
                                    type={'close'}
                                    onClick={handleClose}
                                    className={styles.ButtonFullWidth}
                                />
                            )}
                        </div>
                    </DialogBody>
                </Dialog>
                {canEditRegular && isCancelAbsenceDialogOpen && (
                    <CancelAbsenceRegularSlot
                        open={isCancelAbsenceDialogOpen}
                        closeParent={handleClose}
                        handleClose={() => setCancelAbsenceDialogOpen(false)}
                        refetchData={refetchData}
                        absences={absences}
                        daycare={daycare}
                    />
                )}
            </>
        )) || <></>
    )
}

export default ShowAbsenceDialog
